// Slack sub-design system
// Atoms used to compose Slack-native modals, channel messages, and the App Home tab.
// Wrap any usage in <div className="slack-root">…</div> so the scoped tokens take effect.

import React, { useState, useEffect, Fragment } from 'react';

/* ──────────────────────────────────────────────
   Inline text primitives
   ────────────────────────────────────────────── */
function SkLink({ href = '#', soft = false, children }) {
  return <a href={href} className={soft ? 'sk-link-soft' : 'sk-link'}>{children}</a>;
}

function SkMention({ children, awaiting = false }) {
  return <span className={awaiting ? 'sk-awaiting-pill' : 'sk-mention'}>@{children}</span>;
}

function SkCode({ children }) {
  return <code className="sk-code">{children}</code>;
}

/* ──────────────────────────────────────────────
   Glyphs (inline SVG, no icon font)
   ────────────────────────────────────────────── */
function IcoTask() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="1.5" width="13" height="13" rx="2" fill="#1868DB"/>
      <path d="M5 8.2l2.2 2.2L11.2 6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IcoPriority() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2" y="5" width="12" height="2" rx="1" fill="#E97F0E"/>
      <rect x="2" y="9" width="12" height="2" rx="1" fill="#E97F0E"/>
    </svg>
  );
}

function IcoMegaphone() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2 6v4l7 3V3z" fill="#E97F0E" stroke="#7A3F00" strokeWidth=".75" strokeLinejoin="round"/>
      <path d="M9 6.5c1 .2 1.6 1 1.6 1.5s-.6 1.3-1.6 1.5" stroke="#7A3F00" strokeWidth="1" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function IcoMore() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
      <circle cx="4" cy="8" r="1.3"/><circle cx="8" cy="8" r="1.3"/><circle cx="12" cy="8" r="1.3"/>
    </svg>
  );
}

function IcoX() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IcoExpand() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M3 9v3.5h3.5M13 7V3.5H9.5M3 12.5L7 8.5M13 3.5L9 7.5"/>
    </svg>
  );
}

/* ──────────────────────────────────────────────
   Avatars
   ────────────────────────────────────────────── */
function SkAppIcon({ src }) {
  return <span className="sk-app-icon">{src ? <img src={src} alt="" /> : null}</span>;
}

function SkUserAvatar({ src, initials = '?' }) {
  if (src) return <img src={src} alt="" className="sk-user-avatar" />;
  return <span className="sk-user-avatar sk-user-avatar-fallback">{initials}</span>;
}

/* ──────────────────────────────────────────────
   Modal shell
   ────────────────────────────────────────────── */
function SkModal({ title, iconSrc, children, actions }) {
  return (
    <div className="sk-modal" role="dialog" aria-modal="true">
      <div className="sk-modal-header">
        <SkAppIcon src={iconSrc} />
        <div className="sk-title">{title}</div>
        <div className="sk-head-actions">
          <button type="button" aria-label="Open in workspace"><IcoExpand /></button>
          <button type="button" aria-label="Close"><IcoX /></button>
        </div>
      </div>
      <div className="sk-modal-body">{children}</div>
      {actions && <div className="sk-modal-footer">{actions}</div>}
    </div>
  );
}

/* ──────────────────────────────────────────────
   App Home (tab surface)
   ────────────────────────────────────────────── */
function SkAppHome({ appName = 'Groomba', iconSrc, tabs = ['Home', 'Messages', 'About'], activeTab = 'Home', children }) {
  return (
    <div className="sk-apphome">
      <div className="sk-apphome-chrome">
        <div className="sk-apphome-chrome-left">
          <button type="button" className="sk-apphome-star" aria-label="Star app">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
              <path d="M8 2l1.76 3.57 3.94.57-2.85 2.78.67 3.92L8 10.98 4.48 12.84l.67-3.92L2.3 6.14l3.94-.57z"/>
            </svg>
          </button>
          <SkAppIcon src={iconSrc} />
          <span className="sk-apphome-name">{appName}</span>
        </div>
        <div className="sk-apphome-chrome-right">
          <button type="button" aria-label="Notifications">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
              <path d="M4 11V7.5a4 4 0 0 1 8 0V11l1 1.5H3z"/>
              <path d="M6.5 13.5a1.5 1.5 0 0 0 3 0"/>
            </svg>
          </button>
          <button type="button" aria-label="More"><IcoMore /></button>
          <button type="button" aria-label="Close"><IcoX /></button>
        </div>
      </div>
      <div className="sk-apphome-tabs" role="tablist">
        {tabs.map(t => (
          <button type="button" key={t} role="tab" aria-selected={t === activeTab}
            className={`sk-apphome-tab ${t === activeTab ? 'active' : ''}`}>{t}</button>
        ))}
      </div>
      <div className="sk-apphome-body">{children}</div>
    </div>
  );
}

function SkAppHomeSection({ title, children }) {
  return (
    <section className="sk-apphome-section">
      <h3 className="sk-apphome-section-title">{title}</h3>
      <div className="sk-apphome-section-body">{children}</div>
    </section>
  );
}

function SkAppHomeActions({ actions = [] }) {
  return (
    <div className="sk-apphome-actions">
      {actions.map((a, i) => (
        <button type="button" key={i} className={`sk-apphome-action ${a.variant || 'outline'}`}>{a.label}</button>
      ))}
    </div>
  );
}

function SkAppHomePoll({ id, href = '#', title, meta = [], note, noteTone = 'alert' }) {
  return (
    <div className="sk-apphome-poll">
      <div className="sk-apphome-poll-title"><a href={href}>[{id}] {title}</a></div>
      <div className="sk-apphome-poll-meta">
        {meta.map((m, i) => (
          <Fragment key={i}>
            {i > 0 && <span className="sk-apphome-poll-sep" aria-hidden="true" />}
            <span className="sk-apphome-poll-meta-item">
              {m.icon && <span className="ico">{m.icon}</span>}
              {m.avatar && <img src={m.avatar} alt="" />}
              {m.label ? <>{m.label}: <strong>{m.value}</strong></> : <strong>{m.value}</strong>}
            </span>
          </Fragment>
        ))}
      </div>
      {note && (
        <div className={`sk-apphome-poll-note tone-${noteTone}`}>
          {noteTone === 'alert' && <span className="sk-apphome-alert-ico" aria-hidden="true">!</span>}
          <span>{note}</span>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Form atoms
   ────────────────────────────────────────────── */
function SkField({ label, optional, help, children }) {
  return (
    <div className="sk-field">
      {label && (
        <label className="sk-label">
          {label}{optional && <span className="sk-optional"> (optional)</span>}
        </label>
      )}
      {children}
      {help && <div className="sk-help">{help}</div>}
    </div>
  );
}

function SkRow({ label, optional, children }) {
  return (
    <div className="sk-row">
      <label className="sk-label">{label}{optional && <span className="sk-optional"> (optional)</span>}</label>
      <div className="sk-right">{children}</div>
    </div>
  );
}

function SkInput({ value, counter, ...rest }) {
  return (
    <div className="sk-input-wrap">
      <input className="sk-input" defaultValue={value} {...rest} />
      {counter != null && <span className="sk-counter">{counter}</span>}
    </div>
  );
}

function SkSelect({ compact, focused, value, options = [] }) {
  return (
    <select className={`sk-select ${compact ? 'compact' : ''} ${focused ? 'focused' : ''}`.trim()} defaultValue={value}>
      {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
    </select>
  );
}

function SkCheck({ checked = true, title, desc }) {
  return (
    <label className="sk-check">
      <input type="checkbox" defaultChecked={checked} />
      <span className="sk-check-body">
        <span className="sk-check-title">{title}</span>
        {desc && <span className="sk-check-desc">{desc}</span>}
      </span>
    </label>
  );
}

function SkUserField({ users = [] }) {
  return (
    <div className="sk-user-field">
      {users.map((u, i) => (
        <span className="sk-user-token" key={i}>
          <SkUserAvatar src={u.src} initials={u.initials} />
          <span>{u.name}</span>
          <button type="button" className="sk-user-x" aria-label={`Remove ${u.name}`}><IcoX /></button>
        </span>
      ))}
    </div>
  );
}

function SkChipField({ chips = [], focused = true }) {
  return (
    <div className={`sk-chip-field ${focused ? 'focused' : ''}`}>
      {chips.map((c, i) => (
        <span className="sk-chip" key={i}>
          <span>{c}</span>
          <button type="button" className="sk-chip-x" aria-label={`Remove ${c}`}><IcoX /></button>
        </span>
      ))}
      {focused && <span className="sk-chip-caret" aria-hidden="true" />}
    </div>
  );
}

function SkBtn({ primary, small, danger, children, ...rest }) {
  const cls = ['sk-btn', primary && 'primary', small && 'small', danger && 'danger'].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest}>{children}</button>;
}

function SkHint({ children, style }) {
  return <p className="sk-hint" style={style}>{children}</p>;
}

function SkDivider() { return <hr className="sk-divider" />; }

/* ──────────────────────────────────────────────
   Channel message + poll atoms
   ────────────────────────────────────────────── */
function SkMessage({ appName = 'Groomba', isApp = true, time = '9:32 PM', iconSrc, grouped = false, actions, children }) {
  return (
    <div className={`sk-message ${grouped ? 'grouped' : ''}`}>
      <div className="sk-msg-avatar">
        {grouped ? null : (iconSrc
          ? <img src={iconSrc} alt="" />
          : <div className="sk-avatar-fallback" />)}
      </div>
      {!grouped && (
        <div className="sk-msg-head">
          <span className="sk-msg-name">{appName}</span>
          {isApp && <span className="sk-app-tag">App</span>}
          <span className="sk-msg-time">{time}</span>
        </div>
      )}
      <div className="sk-msg-body">{children}</div>
      <div className="sk-msg-actions">
        {actions != null ? actions : <button type="button" aria-label="More"><IcoMore /></button>}
      </div>
    </div>
  );
}

function SkIssueTitle({ id, href = '#', children }) {
  return <div className="sk-issue-title"><a href={href}>[{id}] {children}</a></div>;
}

function SkMetaRow({ items }) {
  return (
    <div className="sk-meta-row">
      {items.map((it, i) => (
        <span className="sk-meta" key={i}>
          {it.icon && <span className="sk-meta-ico">{it.icon}</span>}
          {it.avatar && <img src={it.avatar} alt="" />}
          <span>{it.label ? <>{it.label}: <strong>{it.value}</strong></> : it.value}</span>
        </span>
      ))}
    </div>
  );
}

function SkVoteRow({ options, chosen, layout = 'flex' }) {
  return (
    <div className={`sk-vote-row layout-${layout}`}>
      {options.map(v => (
        <button type="button" key={v} className={`sk-vote ${chosen === v ? 'chosen' : ''}`}>{v}</button>
      ))}
    </div>
  );
}

function SkAwaiting({ mentions }) {
  return (
    <div className="sk-awaiting-row">
      <span className="sk-megaphone"><IcoMegaphone /></span>
      <span>Awaiting: {mentions.map((m, i) => (
        <Fragment key={i}>
          {i > 0 && ', '}
          <SkMention awaiting>{m}</SkMention>
        </Fragment>
      ))}</span>
    </div>
  );
}

function SkClosing({ time = 'Today at 9:32 PM', issueId, action = 'deferred' }) {
  return (
    <p className="sk-closing">
      Polling closes {time}. If polling closes now, <SkLink>{issueId}</SkLink> will be {action}.
    </p>
  );
}

// Animated voter lines — "@Name voted X.Y story points." appear one-by-one then loop.
// Pass `static` to render every voter immediately (used in docs figures).
function SkLiveVoters({ voters = [], interval = 1400, startDelay = 400, static: isStatic = false }) {
  const [count, setCount] = useState(isStatic ? voters.length : 0);
  useEffect(() => {
    if (isStatic) return undefined;
    let t;
    const tick = () => {
      setCount(c => (c >= voters.length ? 0 : c + 1));
      t = setTimeout(tick, interval);
    };
    t = setTimeout(tick, startDelay);
    return () => clearTimeout(t);
  }, [voters.length, interval, startDelay, isStatic]);
  return (
    <div className="sk-vote-lines" aria-live="polite">
      {voters.slice(0, count).map((v, i) => (
        <div className="sk-vote-line" key={i}>
          <SkMention>{v.name}</SkMention> voted <strong>{v.value}</strong> story points.
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Overflow menu (… → dropdown)
   ────────────────────────────────────────────── */
function SkOverflowMenu({ items = [], align = 'left', open = false, static: isStatic = true, label = 'More actions' }) {
  return (
    <span className={`sk-overflow ${align === 'right' ? 'align-right' : 'align-left'} ${open || isStatic ? 'is-open' : ''}`}>
      <button type="button" className="sk-overflow-trigger" aria-label={label} aria-haspopup="menu" aria-expanded={open || isStatic}>
        <IcoMore />
      </button>
      {(open || isStatic) && (
        <div className="sk-overflow-menu" role="menu">
          {items.map((it, i) => (
            <button type="button" key={i} role="menuitem"
              className={`sk-overflow-item ${it.danger ? 'danger' : ''}`}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/* ──────────────────────────────────────────────
   Stage + Compose
   ────────────────────────────────────────────── */
function SkStage({ variant = 'default', children }) {
  return <div className={`sk-stage ${variant}`.trim()}>{children}</div>;
}

function SkCompose({ command, showCaret = true }) {
  return (
    <div className="sk-compose" role="presentation">
      <span className="sk-compose-text">
        <span className="cmd">{command}</span>
        {showCaret && <span className="caret" aria-hidden="true" />}
      </span>
      <span className="sk-compose-send" role="button" aria-label="Send">
        <span className="ico">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M2.5 3.5 17.5 10 2.5 16.5 2.5 11.5 11.5 10 2.5 8.5Z" />
          </svg>
        </span>
      </span>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Figure wrapper — embeds a Slack surface inline in marketing/docs prose.
     variant="modal"   → lilac wash, modal floats on top
     variant="channel" → white canvas with a faux channel header band
                         (header text comes from the `channel` prop)
     variant="home"    → lilac wash sized for the App Home tab surface
   The inner structure mirrors the design system literally: figure →
   slack-root → sk-stage → composition.
   ────────────────────────────────────────────── */
function SkFigure({ variant = 'modal', channel, hasMenu = false, caption, children }) {
  const cls = ['sk-figure', variant, hasMenu && 'has-menu'].filter(Boolean).join(' ');
  return (
    <figure className={cls}>
      <div className="slack-root">
        <div className="sk-stage" data-channel={channel}>
          {variant === 'channel' ? <div>{children}</div> : children}
        </div>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

export {
  SkLink, SkMention, SkCode,
  SkAppIcon, SkUserAvatar,
  IcoTask, IcoPriority, IcoMegaphone, IcoMore, IcoX, IcoExpand,
  SkModal, SkField, SkRow, SkInput, SkSelect, SkCheck,
  SkUserField, SkChipField, SkBtn, SkHint, SkDivider,
  SkAppHome, SkAppHomeSection, SkAppHomeActions, SkAppHomePoll,
  SkMessage, SkIssueTitle, SkMetaRow, SkVoteRow, SkAwaiting, SkClosing,
  SkLiveVoters, SkOverflowMenu, SkStage, SkCompose, SkFigure,
};
